import {
    call,
    put,
    select,
    takeLeading,
} from 'redux-saga/effects';

import { PayloadAction } from '@reduxjs/toolkit';

import {
    addEmployment,
    addEmploymentFulfilled,
    addEmploymentRejected,
    getEmployments,
    getEmploymentsFulfilled,
    getEmploymentsRefData,
    getEmploymentsRefDataFulfilled,
    getEmploymentsRefDataRejected,
    getEmploymentsRejected,
    submitEmployments,
    submitEmploymentsFulfilled,
    submitEmploymentsRejected,
    updateEmployment,
    updateEmploymentFulfilled,
    updateEmploymentRejected,
    validateEmployments,
    validateEmploymentsFulfilled,
    validateEmploymentsRejected,
} from './employmentsSlice';
import {
    addEmploymentApi,
    fetchEmployments,
    fetchEmploymentsRefData,
    submitEmploymentsApi,
    updateEmploymentApi,
    validateEmploymentsApi,
} from './employmentsApi';
import {
    selectEmploymentAddUrl,
    selectEmploymentsRefDataUrl,
    selectEmploymentsSubmitUrl,
    selectEmploymentsValidateUrl,
    selectEmploymentUpdateUrl,
    selectGetEmploymentsUrl,
} from './employmentsSelectors';
import {
    EmploymentDetails,
    EmploymentsRefData,
    EmploymentUpsertApiResponse,
    ValidationResponse,
} from './employmentsTypes';
import { ApiResponse } from '../../utils';

function* getEmploymentsWorkSaga() {
    try {
        const url: string = yield select(selectGetEmploymentsUrl);
        const { data }: ApiResponse<Record<number, EmploymentDetails>> = yield call(fetchEmployments, url);

        yield put(getEmploymentsFulfilled(data));
    } catch (e) {
        yield put(getEmploymentsRejected());
    }
}

function* getEmploymentsWatchSaga() {
    yield takeLeading(getEmployments.type, getEmploymentsWorkSaga);
}

function* getEmploymentsRefDataWorkSaga() {
    try {
        const url: string = yield select(selectEmploymentsRefDataUrl);
        const { data }: ApiResponse<EmploymentsRefData> = yield call(fetchEmploymentsRefData, url);

        yield put(getEmploymentsRefDataFulfilled(data));
    } catch (e) {
        yield put(getEmploymentsRefDataRejected());
    }
}

function* getEmploymentsRefDataWatchSaga() {
    yield takeLeading(getEmploymentsRefData.type, getEmploymentsRefDataWorkSaga);
}

function* addEmploymentWorkSaga(action: PayloadAction<EmploymentDetails>) {
    try {
        const url: string = yield select(selectEmploymentAddUrl);

        const { data }: ApiResponse<EmploymentUpsertApiResponse> = yield call(addEmploymentApi, url, action.payload);

        if (data.success) {
            if (data.employments) {
                yield put(getEmploymentsFulfilled(data.employments));
            }
            yield put(addEmploymentFulfilled(action.payload));
        } else {
            yield put(addEmploymentRejected());
        }
    } catch (e) {
        yield put(addEmploymentRejected());
    }
}

function* addEmploymentWatchSaga() {
    yield takeLeading(addEmployment.type, addEmploymentWorkSaga);
}

function* updateEmploymentWorkSaga(action: PayloadAction<EmploymentDetails>) {
    try {
        const url: string = yield select(selectEmploymentUpdateUrl);

        const { data }: ApiResponse<EmploymentUpsertApiResponse> = yield call(
            updateEmploymentApi, 
            url.replace('{employmentId}', action.payload.employmentId?.toString() ?? '0'), 
            action.payload,
        );

        if (data.success) {
            if (data.employments) {
                yield put(getEmploymentsFulfilled(data.employments));
            }
            yield put(updateEmploymentFulfilled(action.payload));
        } else {
            yield put(updateEmploymentRejected());
        }
    } catch (e) {
        yield put(updateEmploymentRejected());
    }
}

function* updateEmploymentWatchSaga() {
    yield takeLeading(updateEmployment.type, updateEmploymentWorkSaga);
}

function* validateEmploymentsWorkSaga(action: PayloadAction<Record<number, EmploymentDetails>>) {
    try {
        const url: string = yield select(selectEmploymentsValidateUrl);

        const { data } = yield call(validateEmploymentsApi, url, action.payload);

        yield put(validateEmploymentsFulfilled({
            ...data,
        } as ValidationResponse));
    } catch (e) {
        yield put(validateEmploymentsRejected());
    }
}

function* validateEmploymentsWatchSaga() {
    yield takeLeading(validateEmployments.type, validateEmploymentsWorkSaga);
}

function* submitEmploymentsWorkSaga() {
    const url: string | undefined = yield select(selectEmploymentsSubmitUrl);

    if (url) {
        try {
            yield call(submitEmploymentsApi, url);
            yield put(submitEmploymentsFulfilled());
        } catch (e) {
            yield put(submitEmploymentsRejected());
        }
    } else {
        yield put(submitEmploymentsFulfilled());
    }
}

function* submitEmploymentsWatchSaga() {
    yield takeLeading(submitEmployments.type, submitEmploymentsWorkSaga);
}

export const employmentsSagas = [
    getEmploymentsWatchSaga,
    getEmploymentsRefDataWatchSaga,
    addEmploymentWatchSaga,
    updateEmploymentWatchSaga,
    validateEmploymentsWatchSaga,
    submitEmploymentsWatchSaga,
];
